import { Link } from "react-router-dom";
import { MapPin, Phone, Clock, Facebook, Mail, ArrowUp } from "lucide-react";

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-[#0F2557] text-white relative overflow-hidden" data-testid="footer">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />
      </div>

      {/* Scroll to top button */}
      <button
        onClick={scrollToTop}
        className="absolute top-6 left-1/2 -translate-x-1/2 w-14 h-14 bg-white text-[#0F2557] flex items-center justify-center shadow-xl transition-all duration-300 hover:scale-110 hover:shadow-2xl z-10 rounded-full"
        aria-label="Scroll to top"
      >
        <ArrowUp className="h-6 w-6" />
      </button>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-32 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Company Info */}
          <div className="space-y-6">
            <img 
              src="/images/logo.webp" 
              alt="Fox Precision Engineering Ltd"
              width="600"
              height="155"
              loading="lazy"
              decoding="async"
              className="h-14 w-auto brightness-0 invert"
            />
            <p className="text-white/60 text-sm leading-relaxed">
              A family-run sub-contract precision engineering company in Hull. Delivering bespoke CNC machined components to UK industry — on time, every time.
            </p>
            <div className="flex items-center gap-3">
              <a
                href="https://www.facebook.com/FoxPrecisionEngineeringLtd/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 border border-white/20 flex items-center justify-center hover:bg-white hover:text-[#0F2557] transition-all duration-300"
                data-testid="facebook-link"
                aria-label="Facebook"
              >
                <Facebook className="h-4 w-4" />
              </a>
              <a
                href="mailto:enquiries@fox-precision-eng.co.uk"
                className="w-10 h-10 border border-white/20 flex items-center justify-center hover:bg-white hover:text-[#0F2557] transition-all duration-300"
                data-testid="email-link"
                aria-label="Email"
              >
                <Mail className="h-4 w-4" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-6">
            <h3 className="text-xs font-bold tracking-[0.2em] uppercase text-white/40">
              Quick Links
            </h3>
            <nav className="flex flex-col gap-3">
              {[
                { name: 'Home', href: '#home' },
                { name: 'About Us', href: '#about' },
                { name: 'Services', href: '#services' },
                { name: 'Products', href: '#products' },
                { name: 'Machinery', href: '#machinery' },
                { name: 'Contact', href: '#contact' },
              ].map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={(e) => scrollToSection(e, link.href)}
                  className="text-white/60 hover:text-white transition-colors text-sm hover:translate-x-1 transform duration-300 inline-block"
                >
                  {link.name}
                </a>
              ))}
            </nav>
          </div>

          {/* Services */}
          <div className="space-y-6">
            <h3 className="text-xs font-bold tracking-[0.2em] uppercase text-white/40">
              Services
            </h3>
            <nav className="flex flex-col gap-3">
              {[
                'CNC Milling',
                'CNC Turning',
                'Grinding & Finishing',
                'Coded Welding',
                'Rapid Prototyping',
              ].map((service) => (
                <a
                  key={service}
                  href="#services"
                  onClick={(e) => scrollToSection(e, '#services')}
                  className="text-white/60 hover:text-white transition-colors text-sm hover:translate-x-1 transform duration-300 inline-block"
                >
                  {service}
                </a>
              ))}
            </nav>
          </div>

          {/* Contact Info */}
          <div className="space-y-6">
            <h3 className="text-xs font-bold tracking-[0.2em] uppercase text-white/40">
              Contact Us
            </h3>
            <div className="space-y-4">
              <div className="flex items-start gap-4 group">
                <div className="w-10 h-10 border border-white/20 flex items-center justify-center flex-shrink-0 group-hover:bg-white group-hover:text-[#0F2557] transition-all duration-300">
                  <MapPin className="h-4 w-4" />
                </div>
                <div className="text-sm text-white/60">
                  82–84 York Street<br />
                  Hull HU2 0QD<br />
                  East Yorkshire
                </div>
              </div>
              <div className="flex items-center gap-4 group">
                <div className="w-10 h-10 border border-white/20 flex items-center justify-center flex-shrink-0 group-hover:bg-white group-hover:text-[#0F2557] transition-all duration-300">
                  <Phone className="h-4 w-4" />
                </div>
                <a
                  href="tel:01482221098"
                  className="text-sm text-white/60 hover:text-white transition-colors"
                  data-testid="footer-phone"
                >
                  01482 221098
                </a>
              </div>
              <div className="flex items-center gap-4 group">
                <div className="w-10 h-10 border border-white/20 flex items-center justify-center flex-shrink-0 group-hover:bg-white group-hover:text-[#0F2557] transition-all duration-300">
                  <Mail className="h-4 w-4" />
                </div>
                <a
                  href="mailto:enquiries@fox-precision-eng.co.uk"
                  className="text-sm text-white/60 hover:text-white transition-colors break-all"
                  data-testid="footer-email"
                >
                  enquiries@fox-precision-eng.co.uk
                </a>
              </div>
              <div className="flex items-start gap-4 group">
                <div className="w-10 h-10 border border-white/20 flex items-center justify-center flex-shrink-0 group-hover:bg-white group-hover:text-[#0F2557] transition-all duration-300">
                  <Clock className="h-4 w-4" />
                </div>
                <div className="text-sm text-white/60">
                  Monday – Friday<br />
                  Open until 5pm
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/40 text-sm">
              © {currentYear} Fox Precision Engineering LTD. All rights reserved.
            </p>
            <div className="flex items-center gap-4 text-xs text-white/40">
              <span>Precision Engineering Hull</span>
              <span className="w-1 h-1 bg-white/40 rounded-full" />
              <span>CNC Machining Hull</span>
              <span className="w-1 h-1 bg-white/40 rounded-full" />
              <span>UK</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
